import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Reveal, SectionKicker } from "./Reveal";
import { imgSrc } from "@/lib/constants";

export const LookbookSection = ({ onOpen }) => {
  const { t, lang } = useLang();
  const [looks, setLooks] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/looks").then((res) => setLooks(res.data)).catch(() => {});
    api.get("/products").then((res) => setProducts(res.data)).catch(() => {});
  }, []);

  if (!looks.length) return null;
  const byCode = Object.fromEntries(products.map((p) => [p.code, p]));

  return (
    <section id="lookbook" data-testid="lookbook-section" className="border-t border-edge bg-paperalt/50">
      <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
        <Reveal>
          <SectionKicker>{t.lookbook.kicker}</SectionKicker>
          <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl" data-testid="lookbook-title">
            {t.lookbook.title}
          </h2>
        </Reveal>
        <div className="mt-14 flex flex-col gap-20 md:gap-28">
          {looks.map((look, i) => {
            const pieces = (look.product_codes || []).map((c) => byCode[c]).filter(Boolean);
            const flip = i % 2 === 1;
            return (
              <Reveal key={look.id}>
                <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-12 md:gap-12">
                  <div className={`relative md:col-span-7 ${flip ? "md:order-2" : ""}`}>
                    <div className={`relative border border-edge bg-surface p-3 shadow-[0_2px_16px_rgba(18,20,23,0.06)] ${flip ? "-rotate-[0.5deg]" : "rotate-[0.5deg]"}`}>
                      <span className="absolute -left-px -top-px z-10 h-5 w-5 border-l-2 border-t-2 border-volt" />
                      <span className="absolute -bottom-px -right-px z-10 h-5 w-5 border-b-2 border-r-2 border-volt" />
                      <div className="group overflow-hidden">
                        <img
                          src={imgSrc(look.image_url)}
                          alt={look.title_de}
                          data-testid={`look-image-${i}`}
                          className="h-[300px] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 sm:h-[420px] lg:h-[480px]"
                        />
                      </div>
                      <div className="absolute bottom-6 left-6 bg-surface/90 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-ink backdrop-blur-sm">
                        LOOK/{String(i + 1).padStart(2, "0")}
                      </div>
                    </div>
                  </div>
                  <div className={`md:col-span-5 ${flip ? "md:order-1" : ""}`}>
                    <h3 className="font-display text-xl font-bold tracking-tight text-ink sm:text-2xl" data-testid={`look-title-${i}`}>
                      {lang === "de" ? look.title_de : look.title_en || look.title_de}
                    </h3>
                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-mutedink">
                      {lang === "de" ? look.description_de : look.description_en || look.description_de}
                    </p>
                    <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-mutedink">{t.lookbook.pieces}</div>
                    <div className="mt-3 flex flex-col gap-2">
                      {pieces.map((p) => (
                        <button
                          key={p.code}
                          data-testid={`look-piece-${p.code}`}
                          onClick={() => onOpen(p)}
                          className="group flex items-center justify-between gap-3 border border-edge bg-surface px-4 py-3 text-left transition-colors duration-300 hover:border-volt"
                        >
                          <span className="min-w-0">
                            <span className="block font-mono text-[9px] uppercase tracking-widest text-mutedink">[{p.code}]</span>
                            <span className="block truncate font-grotesk text-sm font-semibold text-ink">{p.title}</span>
                          </span>
                          <span className="flex shrink-0 items-center gap-2 font-mono text-sm font-bold text-volt">
                            €{p.price}
                            <ArrowUpRight size={13} className="text-mutedink transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-volt" />
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
